import { useApolloClient } from '@apollo/client';
import type { Transcription } from '@frameio/graphql-client/generated';
import { readFieldIfReference } from '@frameio/graphql-client/utils';

type TranscriptionWithAsset = Omit<Transcription, 'asset'> & {
  asset?: { id: string; media?: { hlsManifest?: string | null } | null } | null;
};

export const useTranscriptionCacheOperations = () => {
  const { cache } = useApolloClient();

  const handleWriteTranscriptionToCache = (
    transcription: TranscriptionWithAsset
  ) => {
    const newNode = {
      asset: transcription?.asset || null,
      id: transcription?.id || null,
      name: transcription?.name || null,
      locale: transcription?.locale || null,
      encodeStatus: transcription?.encodeStatus || null,
      displayName: transcription?.displayName || null,
      ...(transcription?.json && { json: transcription.json }),
      ...(transcription?.vtt && { vtt: transcription.vtt }),
      ...(transcription?.srt && { srt: transcription.srt }),
      ...(transcription?.text && { text: transcription.text }),
      __typename: 'Transcription',
      lastEditedAt: transcription?.lastEditedAt || null,
      source: transcription?.source || null,
    };

    const asset = {
      __typename: 'Asset',
      id: transcription.asset?.id,
    };

    const updateNodes = (
      existingNodes: any[],
      helpers: {
        isReference: (value: unknown) => boolean;
        readField: (fieldName: string, from: any) => any;
      }
    ) => {
      const { isReference, readField } = helpers;

      const getNodeId = (node: any) =>
        isReference(node) ? readField('id', node) : node?.id;

      const index = existingNodes.findIndex(
        (node) => getNodeId(node) === transcription?.id
      );

      if (index !== -1) {
        return existingNodes.map((node, i) => (i === index ? newNode : node));
      }

      return [newNode, ...existingNodes];
    };

    // We use cache.modify instead of updating fragment so that other
    // fragments using this data can receive updates example: if we update
    // Transcriptions fragment, a TranscriptionEncodeStatus fragment would not
    // receive updates because cache would not be able to identify it because names
    // are different. Instead cache.modify finds the asset entity and updates fields
    cache.modify({
      id: cache.identify(asset),
      fields: {
        localeTranscriptions(value, { isReference, readField, toReference }) {
          const original = readFieldIfReference({
            fieldName: 'localeTranscriptions',
            from: toReference(asset),
            isReference,
            readField,
            value,
          });

          if (!original) return null;

          const updatedNodes = updateNodes(original.nodes, {
            isReference,
            readField,
          });

          return {
            ...original,
            nodes: updatedNodes,
          };
        },
      },
    });
  };

  const handleRemoveTranscriptionFromCache = ({
    assetId,
    transcriptionId,
  }: {
    assetId: string;
    transcriptionId: string | null;
  }) => {
    const asset = {
      __typename: 'Asset',
      id: assetId,
    };

    cache.modify({
      id: cache.identify(asset),
      fields: {
        localeTranscriptions(value, { isReference, readField, toReference }) {
          const original = readFieldIfReference({
            fieldName: 'localeTranscriptions',
            from: toReference(asset),
            isReference,
            readField,
            value,
          });

          if (!original) return null;

          const getNodeId = (node: unknown) =>
            isReference(node)
              ? readField('id', node)
              : (node as { id?: string })?.id;
          const filteredNodes = original.nodes.filter(
            (node: unknown) => getNodeId(node) !== transcriptionId
          );

          return {
            ...original,
            nodes: filteredNodes,
          };
        },
      },
    });
  };

  return {
    handleWriteTranscriptionToCache,
    handleRemoveTranscriptionFromCache,
  };
};
